// AICA Create Project Screen
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useRole } from '@/contexts/RoleContext';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { logError } from '@/lib/logger';
import { Switch } from '@/components/ui/switch';
import { ToggleGrid } from '@/components/ui/toggle-grid';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { FieldHelp } from '@/components/help';
import { PageBody } from '@/components/layout/PageBody';
import { PageHeader } from '@/components/layout/PageHeader';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { useState, useEffect } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { usePromptProfiles } from '@/hooks/usePromptProfiles';
import { scriptsListOptions, scriptsListQueryKey } from '@/lib/query-options';
import { buildAnalysisLanguageOptions } from '@/lib/analysis-language-options';
import {
  Folder,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Phone,
  MessageSquare,
  X,
  Globe,
  FileText,
  ClipboardList,
  FolderOpen,
} from 'lucide-react';
import type { PromptProfile } from '@/lib/api/types';
import { getProfileDisplayName } from '@/features/prompt-builder/profileDisplay';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface ScriptOption {
  id: string;
  name: string;
  category: string;
}

interface ProjectFormData {
  name: string;
  description: string;
  enableCalls: boolean;
  enableChats: boolean;
  language: string;
  promptProfileId: string | null;
  scriptId: string | null;
}

function FormCard({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-bg-elevated bg-bg-surface">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3 sm:px-6">
        <Icon className="h-5 w-5 shrink-0 text-primary" />
        <h4 className="text-sm font-medium text-fg">{title}</h4>
      </div>
      <div className="p-4 sm:p-6">{children}</div>
    </div>
  );
}

function CreateProjectPage() {
  const { t } = useTranslation(['projects', 'common']);
  useDocumentTitle(t('projects:projects.createProject'));
  const navigate = useNavigate();
  const { selectedOrgId, canCreateProject, isLoading: isRoleLoading } = useRole();
  const { apiClient } = useApiClient();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Mirrors PERMISSION_MATRIX.project.create (client/admin/supervisor/owner)
  // and, for a managed subject, the `org` grant: a role outside it or a
  // narrower grant gets 403 from POST /api/v1/projects, so the form is gated
  // here instead of letting the submit fail. Never decide on a loading
  // snapshot — that is what bounced legitimate deep-links before (see
  // lib/route-role-gate.tsx).
  const createDenied = !isRoleLoading && !canCreateProject;

  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    description: '',
    enableCalls: true,
    enableChats: false,
    language: 'auto',
    promptProfileId: null,
    scriptId: null,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const promptProfilesQuery = usePromptProfiles({ enabled: !!selectedOrgId });
  const scriptsQuery = useQuery(scriptsListOptions(apiClient, selectedOrgId));

  useEffect(() => {
    if (createDenied) {
      void navigate({ to: '/projects', replace: true });
    }
  }, [createDenied, navigate]);

  useEffect(() => {
    if (!promptProfilesQuery.error) return;
    logError('projects.create.load_prompt_profiles_failed', {
      component: 'CreateProjectPage',
      error:
        promptProfilesQuery.error instanceof Error
          ? promptProfilesQuery.error.message
          : 'unknown_error',
    });
  }, [promptProfilesQuery.error, promptProfilesQuery.errorUpdatedAt]);

  useEffect(() => {
    if (!scriptsQuery.error) return;
    logError('projects.create.load_scripts_failed', {
      component: 'CreateProjectPage',
      error:
        scriptsQuery.error instanceof Error
          ? scriptsQuery.error.message
          : 'unknown_error',
    });
  }, [scriptsQuery.error, scriptsQuery.errorUpdatedAt]);

  const profiles: PromptProfile[] = promptProfilesQuery.data ?? [];
  const scripts: ScriptOption[] =
    scriptsQuery.data
      ?.filter((script) => script.isActive)
      .map((script) => ({
        id: script.id,
        name: script.name,
        category: script.category,
      })) ?? [];
  const loadingEnrichment = promptProfilesQuery.isLoading || scriptsQuery.isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = t('projects.validation.nameRequired');
    }
    if (!formData.enableCalls && !formData.enableChats) {
      newErrors.channels = t('projects.validation.channelsRequired');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      if (!selectedOrgId) {
        toast.error(t('projects.validation.pleaseSelectOrganization'));
        return;
      }

      const created = await apiClient.projects.createProject({
        name: formData.name,
        description: formData.description || undefined,
        settings: {
          analysisLanguage: formData.language,
          // `channelProcessing`, camelCase — the only key anything reads
          // (projectSettingsSchema, resolveChannelSettings, the channel
          // settings tab). This used to write a snake_case
          // `channel_processing` plus a `channels: ['calls','chats']` array;
          // neither key is in the schema, so Zod stripped both on read and
          // the channel choice made here reached nothing at all.
          //
          // Explicit booleans, not `true | null`: null means "inherit", and
          // inheritance resolves to the system default `true`, so unchecking
          // a channel used to leave it switched ON.
          channelProcessing: {
            call: formData.enableCalls,
            chat: formData.enableChats,
          },
        },
      } as Parameters<typeof apiClient.projects.createProject>[0]);

      const projectId = created.id;
      const postCreate: Promise<unknown>[] = [];

      if (formData.promptProfileId && projectId) {
        postCreate.push(
          apiClient.projects
            .updateProject(projectId, { promptProfileId: formData.promptProfileId })
            .catch((err) => {
              logError('projects.create.set_prompt_profile_failed', {
                projectId,
                error: err instanceof Error ? err.message : 'unknown_error',
              });
            })
        );
      }

      if (formData.scriptId && projectId) {
        postCreate.push(
          apiClient.scripts
            .assignToProject(projectId, formData.scriptId, true)
            .catch((err) => {
              logError(
                'projects.create.assign_script_failed',
                { projectId, scriptId: formData.scriptId },
                err instanceof Error ? err : undefined
              );
            })
        );
      }

      if (postCreate.length > 0) {
        await Promise.allSettled(postCreate);
      }

      toast.success(
        t('projects.success.projectCreated'),
        t('projects.success.projectReady', { name: formData.name })
      );

      // The list this navigates to caches for 5 minutes (projectsListOptions),
      // and it is not mounted right now, so nothing would refetch on its own —
      // the new project stayed invisible until a manual page reload. Invalidate
      // by the ['projects', 'list'] prefix: the full key also carries the
      // pagination object, which this page does not know.
      await queryClient.invalidateQueries({ queryKey: ['projects', 'list'] });

      // Назначение скрипта выше меняет то, что показывает список скриптов:
      // проектные бейджи и маркер «Не назначен» (#498). У списка свой кеш на
      // 5 минут без рефетча по фокусу, поэтому только что назначенный скрипт
      // ещё пять минут значился бы никогда не применяемым.
      if (formData.scriptId) {
        await queryClient.invalidateQueries({
          queryKey: scriptsListQueryKey(selectedOrgId),
        });
      }

      void navigate({ to: '/projects' });
    } catch (err) {
      logError('projects.create.failed', {
        projectName: formData.name,
        error: err instanceof Error ? err.message : 'unknown_error',
      });
      const errorMessage =
        err instanceof Error ? err.message : t('projects.error.failedToCreate');
      setSubmitError(errorMessage);
      toast.error(t('projects.error.failedToCreate'), errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // While the redirect is in flight, render nothing so the form never flashes
  // for a role that cannot submit it.
  if (createDenied) return null;

  return (
    <PageBody>
      <PageHeader
        breadcrumbs={
          <Breadcrumb>
            <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
            <BreadcrumbItem href="/projects">{t('nav.projects')}</BreadcrumbItem>
            <BreadcrumbItem current>{t('projects.createNewProject')}</BreadcrumbItem>
          </Breadcrumb>
        }
        icon={FolderOpen}
        title={t('projects.createNewProject')}
        subtitle={t('projects.createNewProjectDescription')}
      />

      {/* The form keeps its reading width; the page does not centre it. */}
      <div className="max-w-4xl">
        {/* Error Alert */}
        {submitError && (
          <div className="mb-6 rounded-lg border border-danger bg-danger-dim p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-danger shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-danger-fg">
                  {t('projects.error.failedToCreate')}
                </h3>
                <p className="mt-1 text-xs text-fg-muted">{submitError}</p>
              </div>
              <button
                type="button"
                onClick={() => setSubmitError(null)}
                className="text-danger-fg hover:text-danger-hover"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <FormCard icon={Folder} title={t('projects.basicInformation')}>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-fg mb-2">
                  {t('projects.projectNameLabel')}
                  <span aria-hidden="true" className="text-danger ml-0.5">
                    *
                  </span>
                </label>
                <Input
                  id="name"
                  type="text"
                  aria-required="true"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder={t('projects.projectNamePlaceholder')}
                />
                {errors.name && <p className="mt-1 text-sm text-danger">{errors.name}</p>}
              </div>
              <div>
                <label
                  htmlFor="description"
                  className="flex items-center gap-1.5 text-sm font-medium text-fg mb-2"
                >
                  {t('projects.descriptionLabel')}
                  <FieldHelp id="settings.project.description" />
                </label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  minRows={3}
                  maxLength={1000}
                  placeholder={t('projects.projectDescriptionPlaceholder')}
                />
                <div className="mt-1 flex items-center justify-between gap-4">
                  <p className="text-xs text-fg-muted">
                    {t('projects.projectDescriptionHint')}
                  </p>
                  <p
                    className="shrink-0 text-xs text-fg-subtle"
                    data-testid="project-description-count"
                  >
                    {t('common:common.charactersCount', {
                      count: formData.description.length,
                      max: 1000,
                    })}
                  </p>
                </div>
              </div>
            </div>
          </FormCard>

          {/* Channels */}
          <FormCard icon={Phone} title={t('projects.channels')}>
            <ToggleGrid className="gap-4">
              {/* Phone Calls */}
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, enableCalls: !prev.enableCalls }))
                }
                className={`rounded-md border p-4 text-left transition-colors ${
                  formData.enableCalls
                    ? 'border-primary bg-primary-dim'
                    : 'border-border bg-bg-surface hover:bg-accent'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Switch
                    checked={formData.enableCalls}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, enableCalls: checked }))
                    }
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className="h-4 w-4 text-fg" />
                      <span className="text-sm font-medium text-fg">
                        {t('projects.phoneCallsChannel')}
                      </span>
                    </div>
                    <p className="text-xs text-fg-muted">
                      {t('projects.phoneCallsDescription')}
                    </p>
                  </div>
                </div>
              </button>

              {/* Chats */}
              <button
                type="button"
                onClick={() =>
                  setFormData((prev) => ({ ...prev, enableChats: !prev.enableChats }))
                }
                className={`rounded-md border p-4 text-left transition-colors ${
                  formData.enableChats
                    ? 'border-primary bg-primary-dim'
                    : 'border-border bg-bg-surface hover:bg-accent'
                }`}
              >
                <div className="flex items-start gap-3">
                  <Switch
                    checked={formData.enableChats}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({ ...prev, enableChats: checked }))
                    }
                    size="sm"
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <MessageSquare className="h-4 w-4 text-fg" />
                      <span className="text-sm font-medium text-fg">
                        {t('projects.chatsChannel')}
                      </span>
                    </div>
                    <p className="text-xs text-fg-muted">
                      {t('projects.chatsDescription')}
                    </p>
                  </div>
                </div>
              </button>
            </ToggleGrid>
            {errors.channels && (
              <p className="mt-2 text-sm text-danger">{errors.channels}</p>
            )}
          </FormCard>

          {/* Language */}
          <FormCard icon={Globe} title={t('projects.analysisLanguage')}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Из каталога ANALYSIS_LANGUAGES — один список на три экрана;
                рукописная копия здесь предлагала бы язык, который z.enum на
                записи уже не принимает (и наоборот). */}
              {buildAnalysisLanguageOptions(t, 'projects').map((lang) => (
                <button
                  key={lang.value}
                  type="button"
                  aria-pressed={formData.language === lang.value}
                  onClick={() =>
                    setFormData((prev) => ({ ...prev, language: lang.value }))
                  }
                  className={`rounded-md border p-3 text-center transition-colors ${
                    formData.language === lang.value
                      ? 'border-primary bg-primary-dim'
                      : 'border-border bg-bg-surface hover:bg-accent'
                  }`}
                >
                  <p className="text-sm font-medium text-fg">{lang.label}</p>
                </button>
              ))}
            </div>
          </FormCard>

          {/* Prompt Profile & Script */}
          <FormCard
            icon={FileText}
            title={`${t('projects.promptProfile')} & ${t('projects.script')}`}
          >
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="promptProfile"
                  className="block text-sm font-medium text-fg mb-2"
                >
                  {t('projects.promptProfile')}
                </label>
                <Select
                  value={formData.promptProfileId ?? ''}
                  onValueChange={(val) =>
                    setFormData((prev) => ({
                      ...prev,
                      promptProfileId: (val as string) || null,
                    }))
                  }
                  disabled={loadingEnrichment}
                  placeholder={t('projects.selectPromptProfile')}
                  options={profiles.map((p) => ({
                    value: p.id,
                    label: getProfileDisplayName(p, t),
                  }))}
                  size="sm"
                />
              </div>
              <div>
                <label
                  htmlFor="script"
                  className="block text-sm font-medium text-fg mb-2"
                >
                  <div className="flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    {t('projects.script')}
                  </div>
                </label>
                <Select
                  value={formData.scriptId ?? ''}
                  onValueChange={(val) =>
                    setFormData((prev) => ({
                      ...prev,
                      scriptId: (val as string) || null,
                    }))
                  }
                  disabled={loadingEnrichment}
                  placeholder={
                    scripts.length === 0 && !loadingEnrichment
                      ? t('projects.noScriptsAvailable')
                      : t('projects.noScript')
                  }
                  options={scripts.map((s) => ({ value: s.id, label: s.name }))}
                  size="sm"
                />
              </div>
            </div>
          </FormCard>

          {/* Actions */}
          <div className="flex items-center justify-between gap-4 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: '/projects' })}
            >
              <X className="h-4 w-4 mr-2" />
              {t('common:common.cancel')}
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {t('projects.creating')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {t('projects.createProject')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </PageBody>
  );
}

export const Route = createFileRoute('/projects/new')({
  component: CreateProjectPage,
});
